import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ClipboardCheck, PencilLine } from 'lucide-react';
import type { ConditionOnset, DiagnosisPick } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { Encounter } from './types';
import { DiagnosisCard, type DraftDiagnosis } from './DiagnosisCard';
import { DiagnosisPicker } from './DiagnosisPicker';
import { EmrEmpty, EmrPanel } from './EmrPanel';
import { DIAGNOSIS_CERTAINTY, type DiagnosisCertainty } from './labels';

function draftsOf(encounter: Encounter): DraftDiagnosis[] {
  return encounter.diagnoses.map((diagnosis) => ({
    icdCode: diagnosis.icdCode,
    icdTitle: diagnosis.icdTitle,
    isPrimary: diagnosis.isPrimary,
    certainty: (DIAGNOSIS_CERTAINTY as readonly string[]).includes(diagnosis.certainty) ? (diagnosis.certainty as DiagnosisCertainty) : 'provisional',
    ...(diagnosis.conditionOnset ? { conditionOnset: diagnosis.conditionOnset as ConditionOnset } : {}),
  }));
}

export function DiagnosisEditor({
  encounter,
  readOnly,
  onSaved,
  departmentId,
}: {
  encounter: Encounter;
  readOnly: boolean;
  onSaved: () => void;
  departmentId?: string | null;
}) {
  const toast = useToast();
  const [list, setList] = useState<DraftDiagnosis[]>(() => draftsOf(encounter));
  const [dirty, setDirty] = useState(false);

  const change = (next: (current: DraftDiagnosis[]) => DraftDiagnosis[]) => {
    setList(next);
    setDirty(true);
  };
  const add = (pick: DiagnosisPick) =>
    change((current) =>
      current.some((entry) => entry.icdCode === pick.code)
        ? current
        : [...current, { icdCode: pick.code, icdTitle: pick.title, isPrimary: current.length === 0, certainty: 'provisional' }],
    );

  const save = useMutation({
    mutationFn: () => put(`/encounters/${encounter.id}/diagnoses`, { diagnoses: list }),
    onSuccess: () => {
      toast.success(tr('opd', 'diagnoses_saved'));
      setDirty(false);
      onSaved();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_save_the_diagnoses'), describeError(error)),
  });

  const chosen = new Set(list.map((entry) => entry.icdCode));

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      {!readOnly && <DiagnosisPicker readOnly={readOnly} chosen={chosen} departmentId={departmentId} onAdd={add} />}

      <EmrPanel
        icon={ClipboardCheck}
        title={tr('opd', 'this_visit')}
        description={list.length > 0 ? tr('opd', 'dx_count', { count: list.length }) : undefined}
        className={readOnly ? 'xl:col-span-2' : undefined}
        bodyClassName="p-3 sm:p-4"
        actions={
          !readOnly && (
            <>
              {dirty && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2.5 py-1 text-xs text-warning">
                  <PencilLine className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'unsaved_changes')}
                </span>
              )}
              <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
                {tr('opd', 'save_diagnoses')}
              </Button>
            </>
          )
        }
      >
        {list.length === 0 ? (
          <EmrEmpty>{tr('opd', 'no_diagnosis_recorded_one_is_required_before')}</EmrEmpty>
        ) : (
          <ul className="space-y-3">
            {list.map((diagnosis, index) => (
              <DiagnosisCard
                key={diagnosis.icdCode}
                diagnosis={diagnosis}
                readOnly={readOnly}
                onChange={(patch) => change((current) => current.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)))}
                onPrimary={() => change((current) => current.map((entry, at) => ({ ...entry, isPrimary: at === index ? !entry.isPrimary : false })))}
                onRemove={() => change((current) => current.filter((_, at) => at !== index))}
              />
            ))}
          </ul>
        )}
      </EmrPanel>
    </div>
  );
}
